import { AxiosError } from 'axios'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'

import { getQueryParamsObjectToString } from 'utils/helpers/router'
import { downloadFileUrl, downloadFileBlob } from 'utils/downloadFileUrl'
import { toastFailed, toastSuccess } from 'utils/toast'

import {
  fetchSKPListAPI,
  getSKPDetailAPI,
  type SKPDetailsInterface,
  fetchSKPProducts,
  fetchSKPAgreements,
  fetchSKPActualClaims,
  fetchSKPReimbursements,
  fetchSKProofFiles,
  upsertSKPProduct,
  postUploadFile,
  getDownloadSKPDetailsAPI,
  getDownloadSKPCampaignAPI,
  getDownloadActualClaimAPI,
  postUploadActualClaimFileAPI,
  deleteSKPProduct,
  deleteSKPAttachment,
  postApproveActualClaim,
  postReimbursementItem,
  postSKP,
  putUpdateSKPAPI,
  postCopySKPAPI,
  deleteSKPAPI,
  deleteReimbursement,
  putActualQtyClaimItem,
  deleteActualQtyClaimItem,
  fetchCampaignCluster,
  postSubmitSKPFile,
  getUploadFileRules,
  getBaseDiscounts,
  fetchSKPProductDetailsV1,
  postSKPStatusAPI,
  getVendorListAPI,
  mapPaginatedListToLegacyQuery,
  fetchTnCList,
  type SKPProductPayloadInterface,
  type PostItemReimbursementInterface,
  type PutActualClaimPayloadInterface,
  type PostSKPInterface,
  type SKPProductsInterface,
  type PostInternalSKPInterface,
} from 'features/Enterprise/SKP/utils/apiList'
import { type PutUpdateSKPInterface } from 'features/Enterprise/SKP/@types/sourcingTypes'
import { mapInternalSkpToEchoCopyBody } from 'features/Enterprise/SKP/utils/helpers'

import {
  sliceName,
  setLoadingState,
  setSKPList,
  setSKPDetails,
  setSKPNumber,
  setSKPProducts,
  setSKPAgreements,
  setSKPActualClaims,
  setSKPReimbursement,
  setSKPProofFiles,
  setSKPProductDetails,
  setApprovedActualClaimStatus,
  setVendorList,
  setCampaignCluster,
  setBaseDiscounts,
} from './skpSlice'
import { TermsAndConditionsDataInterface } from '../tnc'

const getSkpIdFromStore = (getState: () => StoreStateType) => {
  const state = getState()
  return state.skp.skpDetails.id || state.skpAddNew?.skpDetails?.id || 0
}

const getFileNameFromUrl = (fileUrl: string, fallbackFileName: string) => {
  const fileName = fileUrl.split('/').pop()
  return fileName || fallbackFileName
}

const downloadFileFromStorageUrl = async (fileUrl: string, fallbackFileName: string) => {
  const response = await fetch(fileUrl)

  if (!response.ok) {
    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`)
  }

  const blob = await response.blob()
  const fileName = getFileNameFromUrl(fileUrl, fallbackFileName)

  downloadFileBlob({
    data: blob,
    fileName,
    mimeType:
      (blob.type as
        | 'application/pdf'
        | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        | 'application/vnd.ms-excel'
        | 'application/zip'
        | 'application/*'
        | 'application/json'
        | 'application/geojson'
        | 'text/csv') || 'application/*',
  })
}

export const getSKPList = createAsyncThunk(
  `${sliceName}/getSKPList`,
  async (_, { getState, dispatch }) => {
    try {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: true }))
      const {
        skp: { query },
        router: {
          location: { pathname },
        },
      } = getState() as StoreStateType

      const { limit, offset, ...restQuery } = query
      const pageIndex = offset === 0 ? offset : offset / 10
      const updateQuery = { page_size: limit, page_index: pageIndex + 1, ...restQuery }
      updateQuery.skp_no = query.skp_no?.replaceAll('/', '%2F')
      updateQuery.environment = pathname.includes('internal') ? 'Internal' : 'External'

      const parseQuery = getQueryParamsObjectToString(updateQuery)
      const { data } = await fetchSKPListAPI(parseQuery)
      dispatch(setSKPList(data))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: false }))
    }
  },
)

const mapSkpDetailV2ToLegacy = (
  data: SKPDetailsInterface & { campaing_cluster?: string },
): SKPDetailsInterface => ({
  ...data,
  campaign_cluster: data.campaign_cluster || data.campaing_cluster || '',
})

export const getSKPDetails = createAsyncThunk(
  `${sliceName}/getSKPDetails`,
  async (skpIdOrNo: number | string, { dispatch }) => {
    try {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: true }))
      const isSkpId =
        typeof skpIdOrNo === 'number' || (typeof skpIdOrNo === 'string' && /^\d+$/.test(skpIdOrNo))

      if (isSkpId) {
        const skpId = Number(skpIdOrNo)
        const response = await getSKPDetailAPI(skpId)
        const detail = mapSkpDetailV2ToLegacy(
          response.data.data as unknown as SKPDetailsInterface & { campaing_cluster?: string },
        )
        dispatch(setSKPDetails(detail))
        const convertSKPNo = detail.skp_no?.replaceAll('/', '%2F')
        if (convertSKPNo) {
          dispatch(setSKPNumber(convertSKPNo))
        }
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: false }))
    }
  },
)

export const getSKPProducts = createAsyncThunk(
  `${sliceName}/getSKPProducts`,
  async (
    { offset, limit = '5' }: { skpNumber: string; offset: number; limit?: string },
    { dispatch, getState },
  ) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) return
      const pageSize = Number(limit) || 5
      const { data } = await fetchSKPProducts(skpId, offset, limit)
      dispatch(setSKPProducts(mapPaginatedListToLegacyQuery(data, pageSize, offset)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const getSKPProductDetails = createAsyncThunk(
  `${sliceName}/getSKPProductDetails`,
  async (id: string, { dispatch }) => {
    try {
      dispatch(setLoadingState({ key: 'getProduct', isLoading: true }))
      const { data } = await fetchSKPProductDetailsV1(id)
      const productDetails = data.datas[0]
      const productPayloads = {
        barcode: productDetails.barcode,
        product_name: productDetails.sku_name,
        uom: productDetails.uom,
      }
      dispatch(setSKPProductDetails(productPayloads))
      return productPayloads
    } catch (error) {
      callErrorMsg(error)
      return 'error'
    } finally {
      dispatch(setLoadingState({ key: 'getProduct', isLoading: false }))
    }
  },
)

export const updateSKPProductData = createAsyncThunk(
  `${sliceName}/updateSKPProductData`,
  async (
    {
      skpId,
      payload,
      isUpdate,
      cb,
    }: {
      skpId: string
      payload: SKPProductPayloadInterface
      isUpdate: boolean
      cb: () => void
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await upsertSKPProduct(skpId, payload)
      toastSuccess(isUpdate ? 'Produk item berhasil diubah' : 'Produk item berhasil ditambah')
      cb()
    } catch (error) {
      let errMessage = 'Something went wrong'

      if (error instanceof AxiosError) {
        errMessage = error.response?.data?.error?.message || error.message
      }

      const decodedMessage = decodeURIComponent(errMessage)

      toastFailed(decodedMessage)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const deleteSKPProductItem = createAsyncThunk(
  `${sliceName}/deleteSKPProductItem`,
  async ({ skpId, id }: { skpId: number; id: number }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await deleteSKPProduct(skpId, id)
      toastSuccess('Produk item berhasil dihapus')
      return true
    } catch (error) {
      callErrorMsg(error)
      return rejectWithValue(error)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const getSKPAgreements = createAsyncThunk(
  `${sliceName}/getSKPAgreements`,
  async ({ offset }: { skpNumber: string; offset: number }, { dispatch, getState }) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) return
      const pageSize = 5
      const { data } = await fetchSKPAgreements(skpId, offset)
      dispatch(setSKPAgreements(mapPaginatedListToLegacyQuery(data, pageSize, offset)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const getSKPActualClaims = createAsyncThunk(
  `${sliceName}/getSKPActualClaims`,
  async ({ offset }: { skpNumber: string; offset: number }, { dispatch, getState }) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) return
      const pageSize = 5
      const { data } = await fetchSKPActualClaims(skpId, offset)
      dispatch(setSKPActualClaims(mapPaginatedListToLegacyQuery(data, pageSize, offset)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const getSKPReimbursements = createAsyncThunk(
  `${sliceName}/getSKPReimbursements`,
  async ({ offset }: { skpNumber: string; offset: number }, { dispatch, getState }) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) return
      const pageSize = 5
      const { data } = await fetchSKPReimbursements(skpId, offset)
      dispatch(setSKPReimbursement(mapPaginatedListToLegacyQuery(data, pageSize, offset)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const getSKPProofFiles = createAsyncThunk(
  `${sliceName}/getSKPProofFiles`,
  async ({ offset }: { skpNumber: string; offset: number }, { dispatch, getState }) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) return
      const pageSize = 5
      const { data } = await fetchSKProofFiles(skpId, offset)
      dispatch(setSKPProofFiles(mapPaginatedListToLegacyQuery(data, pageSize, offset)))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const approveActualClaim = createAsyncThunk(
  `${sliceName}/approveActualClaim`,
  async ({ skpId, isApprove }: { skpId: number; isApprove: boolean }, { dispatch }) => {
    try {
      await dispatch(setLoadingState({ key: 'approveActualClaim', isLoading: true }))
      await postApproveActualClaim(skpId, isApprove)
      dispatch(setApprovedActualClaimStatus(isApprove))
      toastSuccess(`Actual Claim Approval successfully ${isApprove ? 'approved' : 'cancelled'}`)
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'approveActualClaim', isLoading: false }))
    }
  },
)

export const createReimbursementItem = createAsyncThunk(
  `${sliceName}/createReimbursementItem`,
  async (
    { skpId, payload }: { skpId: number; payload: PostItemReimbursementInterface },
    { dispatch },
  ) => {
    try {
      await dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await postReimbursementItem(skpId, payload)
      toastSuccess('Actual Quantity Claim is successfully added')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const uploadFile = createAsyncThunk(
  `${sliceName}/uploadFile`,
  async (
    {
      skpNumber,
      promotionType,
      module,
      data,
    }: { skpNumber: string; module: string; promotionType?: string; data: FormData },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'uploadData', isLoading: true }))
      await postUploadFile({
        skpNumber,
        data,
        module,
        promotionType: promotionType
          ? `/${promotionType.toLowerCase().replaceAll(' ', '%20')}`
          : '',
      })
    } catch (error: unknown) {
      const errResponse = error as AxiosError<{ error: string; data: string }>
      const errorMessage = errResponse.response?.data?.error || errResponse.message
      toastFailed(errorMessage)
      if (errResponse.response?.data?.data?.includes('http')) {
        const url = errResponse.response?.data?.data
        const splitUrl = url.split('/')
        const fileName = splitUrl[splitUrl?.length - 1]
        fetch(url)
          .then((response) => response.blob())
          .then((blob) => {
            downloadFileUrl(URL.createObjectURL(blob), fileName)
          })
      }
    } finally {
      dispatch(setLoadingState({ key: 'uploadData', isLoading: false }))
    }
  },
)

export const downloadFile = createAsyncThunk(
  `${sliceName}/downloadFile`,
  async (
    {
      skpId,
      fileName,
      module,
      url,
      type = 'download',
    }: {
      skpId?: number
      fileName: string
      url?: string
      module?: string
      type?: 'download' | 'campaign-download'
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'downloadData', isLoading: true }))
      if (url) {
        await downloadFileFromStorageUrl(url, fileName)
        return
      }

      if (!skpId) {
        throw new Error('downloadFile requires skpId when url is not provided')
      }

      let fileUrl = ''

      if (module === 'skp-detail' && type === 'campaign-download') {
        const response = await getDownloadSKPCampaignAPI(skpId)
        fileUrl = response.data.file_url
      } else if (module === 'skp-detail') {
        const response = await getDownloadSKPDetailsAPI(skpId)
        fileUrl = response.data.file_url
      } else if (module === 'actual-claim') {
        const response = await getDownloadActualClaimAPI(skpId)
        fileUrl = response.data.file_url
      }

      if (fileUrl) {
        fetch(fileUrl)
          .then((response) => response.blob())
          .then((blob) => {
            downloadFileUrl(URL.createObjectURL(blob), fileName)
          })
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'downloadData', isLoading: false }))
    }
  },
)

export const getVendorList = createAsyncThunk(
  `${sliceName}/getVendorList`,
  async (company_name: string, { dispatch }) => {
    try {
      const { data } = await getVendorListAPI({ company_name })
      dispatch(setVendorList(data.datas))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const submitSKP = createAsyncThunk(
  `${sliceName}/submitSKP`,
  async (
    {
      type,
      payload = {} as PostSKPInterface,
      skpNumber = '',
      skpId = 0,
      isDuplicateProduct = false,
    }: {
      type: string
      payload?: PostSKPInterface | PostInternalSKPInterface
      skpNumber?: string
      skpId?: number
      isDuplicateProduct?: boolean
    },
    { dispatch, getState },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'submitSKP', isLoading: true }))
      switch (type) {
        case 'add':
        case 'add-internal-skp':
          await postSKP(payload)
          toastSuccess('SKP successfully created')
          break
        case 'edit': {
          const targetSkpId = skpId || Number(skpNumber)
          const response = await putUpdateSKPAPI(
            payload as unknown as PutUpdateSKPInterface,
            targetSkpId,
          )
          toastSuccess(response.data.message || 'SKP successfully updated')
          break
        }
        case 'edit-internal-skp': {
          const internalPayload = payload as PostInternalSKPInterface
          const { skpDetails, campaignClusterList } = (getState() as StoreStateType).skp
          const targetSkpId = skpId || internalPayload.id || skpDetails.id
          const echoBody = mapInternalSkpToEchoCopyBody(
            internalPayload,
            skpDetails,
            campaignClusterList,
            false,
          )
          const updateParams: PutUpdateSKPInterface = {
            ...echoBody,
            vendor_id: internalPayload.vendor_id || skpDetails.vendor_id,
            is_update_vendor: false,
          }
          await putUpdateSKPAPI(updateParams, targetSkpId)
          toastSuccess('SKP successfully updated')
          break
        }
        case 'delete': {
          await deleteSKPAPI(String(skpId || Number(skpNumber)))
          toastSuccess('SKP successfully deleted')
          break
        }
        case 'duplicate': {
          const response = await postCopySKPAPI(
            payload as unknown as Parameters<typeof postCopySKPAPI>[0],
            skpId || Number(skpNumber),
          )
          toastSuccess(response.data.message || 'SKP successfully duplicated')
          break
        }
        case 'duplicate-internal-skp': {
          const internalPayload = payload as PostInternalSKPInterface
          const { skpDetails, campaignClusterList } = (getState() as StoreStateType).skp
          const sourceSkpId = skpId || skpDetails.id
          const copyParams = mapInternalSkpToEchoCopyBody(
            internalPayload,
            skpDetails,
            campaignClusterList,
            isDuplicateProduct,
          )
          await postCopySKPAPI(copyParams, sourceSkpId)
          toastSuccess('SKP successfully duplicated')
          break
        }
        case 'archive':
        case 'reopen':
          await postSKPStatusAPI(skpId || Number(skpNumber), type.toUpperCase())
          toastSuccess(`SKP ${type} successfully`)
          break
        default:
          break
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'submitSKP', isLoading: false }))
    }
  },
)

export const getPrintSKPData = createAsyncThunk(
  `${sliceName}/getPrintSKPData`,
  async ({ type }: { skpNumber: string; type: string }, { getState }) => {
    try {
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!skpId) {
        return { products: [], tnc: [] }
      }
      const pageSize = 5
      const productsResponse = await fetchSKPProducts(skpId, 0, '5')
      const productsBody = mapPaginatedListToLegacyQuery(productsResponse.data, pageSize, 0)
      const tncQuery = `page_size=5&page_index=1&promotion_type=${type.replaceAll(' ', '%20')}`
      const tncResponse = await fetchTnCList(tncQuery)

      return {
        products: productsBody.data,
        tnc: mapPaginatedListToLegacyQuery(tncResponse.data, pageSize, 0).data,
      } as {
        products: SKPProductsInterface[]
        tnc: TermsAndConditionsDataInterface[]
      }
    } catch (error) {
      return callErrorMsg(error)
    }
  },
)

export const updateSKPStatus = createAsyncThunk(
  `${sliceName}/updateSKPStatus`,
  async (
    {
      type,
      skpId,
      rejectReason = '',
    }: {
      skpId: number
      type: 'submit' | 'approve' | 'reject'
      rejectReason?: string
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: true }))
      if (type === 'submit') {
        await postSKPStatusAPI(skpId, 'SUBMIT_TO_HEAD')
      } else if (type === 'approve') {
        await postSKPStatusAPI(skpId, 'APPROVE_AS_HEAD')
      } else {
        await postSKPStatusAPI(skpId, 'REJECT', rejectReason)
      }
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'getSKP', isLoading: false }))
    }
  },
)

export const deleteSKPDoc = createAsyncThunk(
  `${sliceName}/deleteSKPDoc`,
  async ({ id }: { id: number }, { dispatch, getState }) => {
    try {
      dispatch(setLoadingState({ key: 'downloadData', isLoading: true }))
      const skpId = getSkpIdFromStore(getState as () => StoreStateType)
      if (!Number.isSafeInteger(skpId) || skpId <= 0) {
        throw new Error('A numeric SKP ID is required to delete a document')
      }
      if (!Number.isSafeInteger(id) || id <= 0) {
        throw new Error('A numeric attachment ID is required to delete a document')
      }
      await deleteSKPAttachment(skpId, id)
      toastSuccess('File Deleted')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'downloadData', isLoading: false }))
    }
  },
)

export const deleteSKPReimbursement = createAsyncThunk(
  `${sliceName}/deleteSKPReimbursement`,
  async (
    {
      id,
      skpId,
    }: {
      id: number
      skpId: number
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await deleteReimbursement(id, skpId)
      toastSuccess('Success Edit Actual Claim Item')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const updateActualQtyClaim = createAsyncThunk(
  `${sliceName}/updateActualQtyClaim`,
  async (
    { skpId, payload }: { skpId: number; payload: PutActualClaimPayloadInterface },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await putActualQtyClaimItem(skpId, payload)
      toastSuccess('Actual Claim Item Updated')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const deleteActualQtyClaim = createAsyncThunk(
  `${sliceName}/deleteActualQtyClaim`,
  async (
    {
      id,
      skpId,
    }: {
      id: number
      skpId: number
    },
    { dispatch },
  ) => {
    try {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: true }))
      await deleteActualQtyClaimItem(id, skpId)
      toastSuccess('Actual Claim Item Deleted')
    } catch (error) {
      callErrorMsg(error)
    } finally {
      dispatch(setLoadingState({ key: 'updateProduct', isLoading: false }))
    }
  },
)

export const getCampaignCluster = createAsyncThunk(
  `${sliceName}/getCampaignCluster`,
  async (_, { dispatch }) => {
    try {
      const { data } = await fetchCampaignCluster()
      dispatch(setCampaignCluster(data.data?.campaign_clusters))
    } catch (error) {
      callErrorMsg(error)
    }
  },
)

export const submitSKPFile = createAsyncThunk(
  `${sliceName}/submitSKPFile`,
  async ({ file_url, skpId }: { file_url: string; skpId: number }) => {
    try {
      await postSubmitSKPFile(file_url, skpId)
      toastSuccess('Document berhasil di upload')
    } catch (error) {
      callErrorMsg(error)
      throw error
    }
  },
)

export const submitActualClaimFile = createAsyncThunk(
  `${sliceName}/submitActualClaimFile`,
  async ({ file_url, skpId }: { file_url: string; skpId: number }, { dispatch }) => {
    try {
      dispatch(setLoadingState({ key: 'uploadData', isLoading: true }))
      const response = await postUploadActualClaimFileAPI(skpId, file_url)
      const errorFileUrl = response.data.file_url || ''
      const message = response.data.message || ''
      if (errorFileUrl && message) {
        toastFailed(message)
        if (errorFileUrl.includes('http')) {
          const splitUrl = errorFileUrl.split('/')
          const fileName = splitUrl[splitUrl.length - 1]
          fetch(errorFileUrl)
            .then((resFile) => resFile.blob())
            .then((blob) => {
              downloadFileUrl(URL.createObjectURL(blob), fileName)
            })
        }
        throw new Error(message)
      }
      toastSuccess(message || 'Document berhasil di upload')
    } catch (error) {
      callErrorMsg(error)
      throw error
    } finally {
      dispatch(setLoadingState({ key: 'uploadData', isLoading: false }))
    }
  },
)

export const fetchUploadFileRules = createAsyncThunk(
  `${sliceName}/getUploadFileRules`,
  async (module: string) => {
    try {
      const { data } = await getUploadFileRules(module)
      return data.data
    } catch (error) {
      callErrorMsg(error)
      throw error
    }
  },
)

export const fetchBaseDiscount = createAsyncThunk(
  `${sliceName}/fetchBaseDiscount`,
  async (_, { dispatch }) => {
    try {
      const { data } = await getBaseDiscounts()
      dispatch(setBaseDiscounts(data.datas))
      return true
    } catch (error) {
      callErrorMsg(error)
      throw error
    }
  },
)

export const postSKPStatus = createAsyncThunk(
  `${sliceName}/postSKPStatus`,
  async (data: { skpId: number; status: string; rejectReason?: string }) => {
    try {
      const response = await postSKPStatusAPI(data.skpId, data.status, data.rejectReason)
      toastSuccess(response.data.message)
      return response.data
    } catch (error) {
      callErrorMsg(error)
      return undefined
    }
  },
)
